import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Table,
  Modal,
  Form,
  Input,
  message,
  Statistic,
  Tag,
  Popconfirm,
  Tooltip,
  Upload,
  Steps,
  Select,
  Divider,
  Alert,
  Progress,
  InputNumber,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import { admin } from '../../services/api';
import * as XLSX from 'xlsx';
import '../../styles/global-input-fixes.css';

const { Title, Text } = Typography;
const { Step } = Steps;
const { Option } = Select;
const { Dragger } = Upload;

const RotasManagement = () => {
  const [rotas, setRotas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [editingRota, setEditingRota] = useState(null);
  const [statistics, setStatistics] = useState({
    total: 0,
    porCd: [],
    porUf: []
  });

  // Estados para busca por CEP
  const [searchCep, setSearchCep] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [searchResult, setSearchResult] = useState(null);

  // Estados para filtros da tabela
  const [filterCd, setFilterCd] = useState('');
  const [filterRota, setFilterRota] = useState('');
  const [filterUf, setFilterUf] = useState('');

  // Resetar para a primeira página quando os filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCd, filterRota, filterUf]);

  // Estados para paginação no servidor
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Lógica de filtragem das rotas - Removida filtragem local para usar servidor
  // Mantemos o nome filteredRotas para compatibilidade com o resto do código
  const filteredRotas = rotas;

  // Estados do wizard de importação
  const [currentStep, setCurrentStep] = useState(0);
  const [fileData, setFileData] = useState([]);
  const [fileHeaders, setFileHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    cepinicial: null,
    cepfinal: null,
    rota: null,
    setor: null,
    cd: null,
    uf: null
  });
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState({
    success: 0,
    errors: 0,
    details: []
  });
  const [importLogs, setImportLogs] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [errorCepRanges, setErrorCepRanges] = useState([]);

  // Estados para impressão
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printOptions, setPrintOptions] = useState({
    includeCep: true,
    includeSetor: true
  });
  const [isPrinting, setIsPrinting] = useState(false);

  // Referência para auto-scroll dos logs
  const logsContainerRef = useRef(null);

  // Colunas da tabela de rotas
  const rotasColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'CEP Inicial',
      dataIndex: 'cepinicial',
      key: 'cepinicial',
      render: (cep, record) => record.isGrouped ? '-' : (
        <Tag color="blue">{cep}</Tag>
      ),
    },
    {
      title: 'CEP Final',
      dataIndex: 'cepfinal',
      key: 'cepfinal',
      render: (cep, record) => record.isGrouped ? '-' : (
        <Tag color="blue">{cep}</Tag>
      ),
    },
    {
      title: 'Rota',
      dataIndex: 'rota',
      key: 'rota',
    },
    {
      title: 'Setor',
      dataIndex: 'setor',
      key: 'setor',
      render: (setor, record) => record.isGrouped ? '-' : (
        <Tag color="blue">{setor}</Tag>
      ),
    },
    {
      title: 'CD',
      dataIndex: 'cd',
      key: 'cd',
      render: (cd) => (
        <Tag color="orange">{cd}</Tag>
      ),
    },
    {
      title: 'UF',
      dataIndex: 'uf',
      key: 'uf',
      render: (uf) => (
        <Tag color="purple">{uf}</Tag>
      ),
    },
    {
      title: 'Última Atualização',
      dataIndex: 'dtupdate',
      key: 'dtupdate',
      render: (date, record) => record.isGrouped ? '-' : (date ? new Date(date).toLocaleString('pt-BR') : 'N/A'),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => record.isGrouped ? null : (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditRota(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Ver detalhes">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewRota(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Tem certeza que deseja excluir esta rota?"
            onConfirm={() => handleDeleteRota(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Tooltip title="Excluir">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Carregar dados apenas quando houver filtros preenchidos
  useEffect(() => {
    const hasFilters = filterCd.trim() || filterRota.trim() || filterUf.trim();

    if (!hasFilters) {
      setRotas([]);
      setTotal(0);
      return;
    }

    const timer = setTimeout(() => {
      loadRotas();
    }, 500); // Debounce de 500ms para não sobrecarregar o servidor

    return () => clearTimeout(timer);
  }, [currentPage, pageSize, filterCd, filterRota, filterUf]);

  useEffect(() => {
    loadStatistics();
  }, []);

  // Auto-scroll dos logs para o final quando novos logs sío adicionados
  useEffect(() => {
    if (logsContainerRef.current && importLogs.length > 0) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [importLogs]);

  const loadRotas = async () => {
    const hasFilters = filterCd.trim() || filterRota.trim() || filterUf.trim();

    if (!hasFilters) {
      setRotas([]);
      setTotal(0);
      return;
    }

    try {
      setLoading(true);

      const params = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        cd: filterCd.trim() || undefined,
        rota: filterRota.trim() || undefined,
        uf: filterUf.trim() || undefined
      };

      const response = await admin.getRotas(params);

      if (response && response.data) {
        setRotas(response.data);
        setTotal(response.total || response.data.length);
      } else {
        const data = Array.isArray(response) ? response : [];
        setRotas(data);
        setTotal(data.length);
      }
    } catch (error) {
      console.error('Erro ao carregar rotas:', error);
      message.error('Erro ao carregar rotas');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await admin.getRotasStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleCreateRota = async (values) => {
    try {
      await admin.createRota(values);
      message.success('Rota criada com sucesso!');
      setCreateModalVisible(false);
      createForm.resetFields();
      loadRotas();
      loadStatistics();
    } catch (error) {
      console.error('Erro ao criar rota:', error);
      message.error('Erro ao criar rota');
    }
  };

  const handleEditRota = (rota) => {
    setEditingRota(rota);
    editForm.setFieldsValue(rota);
    setEditModalVisible(true);
  };

  const handleUpdateRota = async (values) => {
    try {
      await admin.updateRota(editingRota.id, values);
      message.success('Rota atualizada com sucesso!');
      setEditModalVisible(false);
      editForm.resetFields();
      setEditingRota(null);
      loadRotas();
      loadStatistics();
    } catch (error) {
      console.error('Erro ao atualizar rota:', error);
      message.error('Erro ao atualizar rota');
    }
  };

  const handleDeleteRota = async (id) => {
    try {
      await admin.deleteRota(id);
      message.success('Rota excluída com sucesso!');
      loadRotas();
      loadStatistics();
    } catch (error) {
      console.error('Erro ao excluir rota:', error);
      message.error('Erro ao excluir rota');
    }
  };

  const handleViewRota = (rota) => {
    Modal.info({
      title: 'Detalhes da Rota',
      content: (
        <div>
          <p><strong>ID:</strong> {rota.id}</p>
          <p><strong>CEP Inicial:</strong> {rota.cepinicial}</p>
          <p><strong>CEP Final:</strong> {rota.cepfinal}</p>
          <p><strong>Rota:</strong> {rota.rota || 'N/A'}</p>
          <p><strong>Setor:</strong> {rota.setor || 0}</p>
          <p><strong>CD:</strong> {rota.cd || 'N/A'}</p>
          <p><strong>UF:</strong> {rota.uf || 'N/A'}</p>
          <p><strong>Última Atualização:</strong> {rota.dtupdate ? new Date(rota.dtupdate).toLocaleString('pt-BR') : 'N/A'}</p>
        </div>
      ),
    });
  };

  // Função para buscar rota por CEP
  const handleSearchByCep = async () => {
    if (!searchCep || searchCep.length !== 8) {
      message.error('Por favor, informe um CEP válido com 8 dígitos');
      return;
    }

    setSearchLoading(true);
    try {
      const result = await admin.getRotasByCep(searchCep);
      if (result) {
        setSearchResult(result);
        setSearchModalVisible(true);
      } else {
        message.info('Nenhuma rota encontrada para este CEP');
        setSearchResult(null);
      }
    } catch (error) {
      console.error('Erro ao buscar rota por CEP:', error);
      message.error('Erro ao buscar rota. Tente novamente.');
    } finally {
      setSearchLoading(false);
    }
  };

  // Função para limpar busca
  // Função para imprimir os dados
  const handlePrint = async () => {
    try {
      setIsPrinting(true);

      let dataToPrint = [];
      const isGroupedMode = !printOptions.includeCep && !printOptions.includeSetor;
      const isSetorGroupedMode = !printOptions.includeCep && printOptions.includeSetor;

      if (isGroupedMode) {
        // Se for modo agrupado (apenas CD, Rota, UF), usamos o endpoint que já agrupa no banco de dados
        const params = {
          cd: filterCd.trim() || undefined,
          rota: filterRota.trim() || undefined,
          uf: filterUf.trim() || undefined
        };
        dataToPrint = await admin.getRotasGrouped(params);
      } else {
        // Se for modo detalhado ou agrupado por Setor, precisamos buscar todos os dados
        let allData = [];
        let offset = 0;
        const limit = 100;
        let hasMore = true;

        while (hasMore) {
          const params = {
            limit,
            offset,
            cd: filterCd.trim() || undefined,
            rota: filterRota.trim() || undefined,
            uf: filterUf.trim() || undefined
          };

          const response = await admin.getRotas(params);
          const batch = response.data || (Array.isArray(response) ? response : []);

          if (batch.length === 0) {
            hasMore = false;
          } else {
            allData = [...allData, ...batch];
            offset += limit;
            if (batch.length < limit) hasMore = false;
            if (offset > 20000) hasMore = false;
          }
        }

        // Se o usuário selecionou Setor mas NíO selecionou CEP, agrupar por CD, Rota, UF e Setor
        if (isSetorGroupedMode) {
          const grouped = {};
          allData.forEach(item => {
            const key = `${item.cd || ''}-${item.rota || ''}-${item.uf || ''}-${item.setor || 0}`;
            if (!grouped[key]) {
              grouped[key] = {
                cd: item.cd,
                rota: item.rota,
                uf: item.uf,
                setor: item.setor
              };
            }
          });
          dataToPrint = Object.values(grouped);
        } else {
          dataToPrint = allData;
        }
      }

      if (dataToPrint.length === 0) {
        message.warning('Não há dados para imprimir com os filtros atuais.');
        return;
      }

      // No modo agrupado, dataToPrint já vem do backend pronto

      // Criar janela de impressão
      const printWindow = window.open('', '_blank');
      const html = `
        <html>
          <head>
            <title>Relatório de Rotas</title>
            <style>
              body { font-family: sans-serif; padding: 20px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
              td.no-border { border-top: none; border-bottom: none; }
              th { background-color: #f2f2f2; }
              h2 { text-align: center; }
              .footer { margin-top: 20px; font-size: 10px; text-align: right; color: #666; }
              @media print {
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            <h2>Relatório de Rotas</h2>
            <p><strong>Filtros aplicados:</strong> 
              ${filterCd ? `CD: ${filterCd} | ` : ''}
              ${filterRota ? `Rota: ${filterRota} | ` : ''}
              ${filterUf ? `UF: ${filterUf}` : ''}
              ${!filterCd && !filterRota && !filterUf ? 'Nenhum' : ''}
            </p>
            <table>
              <thead>
                <tr>
                  <th>CD</th>
                  <th>Rota</th>
                  <th>UF</th>
                  ${printOptions.includeCep ? '<th>CEP Inicial</th><th>CEP Final</th>' : ''}
                  ${printOptions.includeSetor ? '<th>Setor</th>' : ''}
                </tr>
              </thead>
              <tbody>
                ${dataToPrint.map((item, index) => {
        const prevItem = index > 0 ? dataToPrint[index - 1] : null;
        const showCd = !prevItem || item.cd !== prevItem.cd;
        const showRota = !prevItem || item.rota !== prevItem.rota || showCd;
        const showUf = !prevItem || item.uf !== prevItem.uf || showRota || showCd;

        return `
                    <tr>
                      <td class="${!showCd ? 'no-border' : ''}">${showCd ? (item.cd || '-') : ''}</td>
                      <td class="${!showRota ? 'no-border' : ''}">${showRota ? (item.rota || '-') : ''}</td>
                      <td class="${!showUf ? 'no-border' : ''}">${showUf ? (item.uf || '-') : ''}</td>
                      ${printOptions.includeCep ? `<td>${item.cepinicial}</td><td>${item.cepfinal}</td>` : ''}
                      ${printOptions.includeSetor ? `<td>${item.setor || 0}</td>` : ''}
                    </tr>
                  `;
      }).join('')}
              </tbody>
            </table>
            <div class="footer">
              Gerado em: ${new Date().toLocaleString('pt-BR')} - Total: ${dataToPrint.length} ${!printOptions.includeCep ? 'grupos de rotas' : 'registros'}
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();

      // Aguardar carregar estilos e disparar impressão
      printWindow.onload = () => {
        printWindow.print();
        // printWindow.close(); // Opcional
      };

      setPrintModalVisible(false);
    } catch (error) {
      console.error('Erro ao preparar impressão:', error);
      message.error('Erro ao preparar impressão');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleClearSearch = () => {
    setSearchCep('');
    setSearchResult(null);
    setSearchModalVisible(false);
  };

  // Função para limpar filtros
  const handleClearFilters = () => {
    setFilterCd('');
    setFilterRota('');
    setFilterUf('');
    setRotas([]);
    setTotal(0);
  };

  // Funções do wizard de importação
  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length > 0) {
          const headers = jsonData[0];
          const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));

          setFileHeaders(headers);
          setFileData(rows);
          setCurrentStep(1);
          message.success(`Arquivo carregado com sucesso! ${rows.length} linhas encontradas.`);
        } else {
          message.error('Arquivo vazio ou inválido');
        }
      } catch (error) {
        console.error('Erro ao processar arquivo:', error);
        message.error('Erro ao processar arquivo. Verifique se é um arquivo Excel ou CSV válido.');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // Prevent upload
  };

  // Função para adicionar logs
  const addImportLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setImportLogs(prev => [...prev, {
      id: Date.now(),
      message,
      type,
      timestamp
    }]);
  };

  const handleColumnMapping = () => {
    // Validar se todos os campos obrigatórios foram mapeados
    const requiredFields = ['cepinicial', 'cepfinal'];
    const missingFields = requiredFields.filter(field => {
      const value = columnMapping[field];
      return value === null || value === undefined;
    });

    if (missingFields.length > 0) {
      message.error(`Por favor, mapeie os campos obrigatórios: ${missingFields.join(', ')}`);
      return;
    }

    setCurrentStep(2);
  };

  const handleImportData = async () => {
    try {
      setIsImporting(true);
      setImportProgress(0);
      setImportLogs([]);
      setCurrentBatch(0);

      addImportLog('Iniciando processo de importação...', 'info');

      const importData = [];

      // Fase 1: Processar e validar dados do arquivo
      addImportLog(`Processando ${fileData.length} linhas do arquivo...`, 'info');

      for (let i = 0; i < fileData.length; i++) {
        const row = fileData[i];
        const rotaData = {};

        // Mapear colunas
        Object.keys(columnMapping).forEach(field => {
          const columnIndex = columnMapping[field];
          if (columnIndex !== null && columnIndex !== undefined) {
            let value = row[columnIndex];

            // Tratar valores vazios ou undefined
            if (value === undefined || value === null || value === '') {
              return;
            }

            // Converter para string e limpar
            value = String(value).trim();

            // Conversões específicas por campo
            if (field === 'setor' && value) {
              value = parseInt(value) || 0;
            } else if ((field === 'cepinicial' || field === 'cepfinal') && value) {
              // Garantir que CEPs sejam strings de 8 dígitos
              value = String(value).replace(/\D/g, '').padStart(8, '0');
            }

            rotaData[field] = value;
          }
        });

        // Validar dados obrigatórios
        if (rotaData.cepinicial && rotaData.cepfinal) {
          importData.push(rotaData);
        } else {
          addImportLog(`Linha ${i + 1}: Dados obrigatórios faltando (CEP inicial e final) - ignorada`, 'warning');
        }

        // Atualizar progresso da validação (30% do total)
        setImportProgress(Math.round(((i + 1) / fileData.length) * 30));
      }

      addImportLog(`${importData.length} registros válidos encontrados para importação`, 'success');

      if (importData.length === 0) {
        addImportLog('Nenhum registro válido encontrado. Verifique o mapeamento das colunas.', 'error');
        setIsImporting(false);
        return;
      }

      // Fase 2: Importar dados em lotes de 100
      const batchSize = 100;
      const totalBatches = Math.ceil(importData.length / batchSize);
      setTotalBatches(totalBatches);

      addImportLog(`Iniciando importação em ${totalBatches} lotes de até ${batchSize} registros...`, 'info');

      let successCount = 0;
      let errorCount = 0;
      const errorDetails = [];

      for (let i = 0; i < importData.length; i += batchSize) {
        const batch = importData.slice(i, i + batchSize);
        const batchNumber = Math.floor(i / batchSize) + 1;
        setCurrentBatch(batchNumber);

        addImportLog(`Processando lote ${batchNumber}/${totalBatches} (${batch.length} registros)...`, 'info');

        try {
          // Usar o endpoint de importação em lote
          const response = await admin.importRotas(batch);

          if (response && response.message) {
            addImportLog(`Lote ${batchNumber}: ${response.message}`, 'success');
            successCount += response.inserted || batch.length;

            // Capturar faixas de CEP com erro se existirem
            if (response.errorRanges && response.errorRanges.length > 0) {
              setErrorCepRanges(prev => [...prev, ...response.errorRanges]);
              errorCount += response.errorRanges.length;
              addImportLog(`Lote ${batchNumber}: ${response.errorRanges.length} faixas de CEP com erro capturadas`, 'warning');
            }
          } else {
            successCount += batch.length;
            addImportLog(`Lote ${batchNumber}: ${batch.length} registros processados`, 'success');
          }

        } catch (error) {
          addImportLog(`Lote ${batchNumber}: Erro - ${error.message || 'Erro desconhecido'}`, 'error');
          errorCount += batch.length;
          errorDetails.push({
            batch: batchNumber,
            error: error.message || 'Erro desconhecido',
            data: batch
          });
        }

        // Atualizar progresso da importação (70% do total, começando dos 30%)
        const importProgress = 30 + Math.round(((i + batchSize) / importData.length) * 70);
        setImportProgress(Math.min(importProgress, 100));

        // Pequena pausa entre lotes para não sobrecarregar o servidor
        if (i + batchSize < importData.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      addImportLog('Importação concluída!', 'success');
      addImportLog(`Resumo: ${successCount} sucessos, ${errorCount} erros`, 'info');

      setImportResults({
        success: successCount,
        errors: errorCount,
        details: errorDetails
      });

      setCurrentStep(3);
      loadRotas();
      loadStatistics();

    } catch (error) {
      console.error('Erro na importação:', error);
      addImportLog(`Erro crítico: ${error.message || 'Erro desconhecido'}`, 'error');
      message.error('Erro durante a importação');
    } finally {
      setIsImporting(false);
    }
  };

  const exportErrorCepRangesToCSV = () => {
    if (errorCepRanges.length === 0) {
      message.warning('Não há faixas de CEP com erro para exportar');
      return;
    }

    // Criar cabeçalho do CSV
    const headers = ['CEP_Inicial', 'CEP_Final', 'Linha', 'Erro'];

    // Converter dados para formato CSV
    const csvData = errorCepRanges.map(range => [
      range.cepinicial,
      range.cepfinal,
      range.line,
      `"${range.error.replace(/"/g, '""')}"` // Escapar aspas duplas
    ]);

    // Combinar cabeçalho com dados
    const csvContent = [headers, ...csvData]
      .map(row => row.join(','))
      .join('\n');

    // Criar e baixar arquivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `faixas_cep_erro_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success(`${errorCepRanges.length} faixas de CEP com erro exportadas para CSV`);
  };

  const resetImportWizard = () => {
    setCurrentStep(0);
    setFileData([]);
    setFileHeaders([]);
    setColumnMapping({
      cepinicial: null,
      cepfinal: null,
      rota: null,
      setor: null,
      cd: null,
      uf: null
    });
    setImportProgress(0);
    setImportResults({ success: 0, errors: 0, details: [] });
    setImportLogs([]);
    setIsImporting(false);
    setCurrentBatch(0);
    setTotalBatches(0);
    setErrorCepRanges([]);
    setImportModalVisible(false);
  };

  const renderImportStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Alert
              message="Instruções de Importação"
              description="Faça upload de um arquivo Excel (.xlsx) ou CSV contendo os dados das rotas. O arquivo deve conter as colunas: CEP Inicial, CEP Final, Rota, Setor, CD e UF."
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Dragger
              accept=".xlsx,.xls,.csv"
              beforeUpload={handleFileUpload}
              showUploadList={false}
            >
              <p className="ant-upload-drag-icon">
                <FileExcelOutlined style={{ fontSize: 48, color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text">Clique ou arraste o arquivo para esta área</p>
              <p className="ant-upload-hint">
                Suporte para arquivos Excel (.xlsx, .xls) e CSV
              </p>
            </Dragger>
          </div>
        );

      case 1:
        return (
          <div>
            <Alert
              message="Mapeamento de Colunas"
              description={`Arquivo carregado com ${fileData.length} linhas. Mapeie as colunas do arquivo para os campos da tabela de rotas.`}
              type="success"
              style={{ marginBottom: 16 }}
            />

            {/* Tabela com cabeçalho e primeira linha do arquivo */}
            {fileHeaders.length > 0 && fileData.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>
                  Prévia do arquivo:
                </Text>
                <Table
                  size="small"
                  pagination={false}
                  scroll={{ x: true }}
                  dataSource={[fileData[0]]}
                  columns={fileHeaders.map((header, index) => ({
                    title: header,
                    dataIndex: index,
                    key: index,
                    width: 150,
                    ellipsis: true,
                    render: (text) => (
                      <Tooltip title={text}>
                        <span>{text}</span>
                      </Tooltip>
                    )
                  }))}
                  style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px'
                  }}
                />
              </div>
            )}

            <Row gutter={[16, 16]}>
              {Object.keys(columnMapping).map(field => (
                <Col span={12} key={field}>
                  <div>
                    <Text strong>
                      {field.toUpperCase()}
                      {['cepinicial', 'cepfinal'].includes(field) &&
                        <span style={{ color: 'red' }}> *</span>
                      }
                    </Text>
                    <Select
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder={`Selecione a coluna para ${field}`}
                      value={columnMapping[field]}
                      onChange={(value) => setColumnMapping(prev => ({ ...prev, [field]: value }))}
                      allowClear
                    >
                      {fileHeaders.map((header, index) => (
                        <Option key={index} value={index}>
                          {header} (Coluna {index + 1})
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
              ))}
            </Row>

            <Divider />

            <div style={{ textAlign: 'center' }}>
              <Space>
                <Button onClick={() => setCurrentStep(0)}>Voltar</Button>
                <Button type="primary" onClick={handleColumnMapping}>
                  Próximo
                </Button>
              </Space>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <Alert
              message="Confirmar Importação"
              description={`Pronto para importar ${fileData.length} registros. Os dados serío enviados em lotes de 100 registros para otimizar o processo.`}
              type="warning"
              style={{ marginBottom: 16 }}
            />

            {isImporting && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Progresso da importação:</Text>
                  {totalBatches > 0 && (
                    <Text style={{ marginLeft: 8 }}>
                      Lote {currentBatch} de {totalBatches}
                    </Text>
                  )}
                </div>
                <Progress
                  percent={importProgress}
                  status={importProgress === 100 ? 'success' : 'active'}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
              </div>
            )}

            {/* Logs em tempo real durante a importação */}
            {isImporting && importLogs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Logs do processo:</Text>
                <div style={{
                  maxHeight: 200,
                  overflow: 'auto',
                  marginTop: 8,
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  padding: '8px',
                  backgroundColor: '#fafafa'
                }}>
                  {importLogs.slice(-10).map((log) => (
                    <div key={log.id} style={{
                      marginBottom: 4,
                      fontSize: '12px',
                      color: log.type === 'error' ? '#ff4d4f' :
                        log.type === 'warning' ? '#faad14' :
                          log.type === 'success' ? '#52c41a' : '#666'
                    }}>
                      <span style={{ color: '#999' }}>[{log.timestamp}]</span> {log.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <Space>
                <Button
                  onClick={() => setCurrentStep(1)}
                  disabled={isImporting}
                >
                  Voltar
                </Button>
                <Button
                  type="primary"
                  onClick={handleImportData}
                  loading={isImporting}
                  disabled={isImporting}
                >
                  {isImporting ? 'Importando...' : 'Iniciar Importação'}
                </Button>
              </Space>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <Alert
              message="Importação Concluída"
              description={`Importação finalizada. ${importResults.success} registros importados com sucesso, ${importResults.errors} erros.`}
              type={importResults.errors === 0 ? 'success' : 'warning'}
              style={{ marginBottom: 16 }}
            />

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Card>
                  <Statistic
                    title="Sucessos"
                    value={importResults.success}
                    prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card>
                  <Statistic
                    title="Erros"
                    value={importResults.errors}
                    prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Logs completos do processo */}
            {importLogs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Log completo do processo:</Text>
                <div
                  ref={logsContainerRef}
                  style={{
                    maxHeight: 300,
                    overflow: 'auto',
                    marginTop: 8,
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: '#fafafa'
                  }}>
                  {importLogs.map((log) => (
                    <div key={log.id} style={{
                      marginBottom: 6,
                      fontSize: '13px',
                      lineHeight: '1.4',
                      color: log.type === 'error' ? '#ff4d4f' :
                        log.type === 'warning' ? '#faad14' :
                          log.type === 'success' ? '#52c41a' : '#666'
                    }}>
                      <span style={{ color: '#999', fontFamily: 'monospace' }}>
                        [{log.timestamp}]
                      </span> {log.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {importResults.errors > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Detalhes dos erros:</Text>
                <div style={{ maxHeight: 200, overflow: 'auto', marginTop: 8 }}>
                  {importResults.details.map((detail, index) => (
                    <Alert
                      key={index}
                      message={`Linha ${index + 1}: ${detail.error}`}
                      type="error"
                      size="small"
                      style={{ marginBottom: 4 }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Faixas de CEP com erro */}
            {errorCepRanges.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong>Faixas de CEP com erro ({errorCepRanges.length}):</Text>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={exportErrorCepRangesToCSV}
                    type="primary"
                  >
                    Exportar CSV
                  </Button>
                </div>
                <div style={{
                  maxHeight: 200,
                  overflow: 'auto',
                  marginTop: 8,
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  padding: '12px',
                  backgroundColor: '#fff2f0'
                }}>
                  {errorCepRanges.map((errorRange, index) => (
                    <div key={index} style={{
                      marginBottom: 8,
                      padding: '8px',
                      border: '1px solid #ffccc7',
                      borderRadius: '4px',
                      backgroundColor: '#fff1f0'
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#cf1322' }}>
                        CEP: {errorRange.cepinicial} - {errorRange.cepfinal}
                      </div>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        Linha: {errorRange.line}
                      </div>
                      <div style={{ fontSize: '13px', color: '#ff4d4f', marginTop: '4px' }}>
                        Erro: {errorRange.error}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontSize: '12px', color: '#8c8c8c' }}>
                  💡 Estas faixas de CEP foram armazenadas e podem ser utilizadas posteriormente para correção.
                </div>
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Space>
                <Button onClick={() => setCurrentStep(0)}>
                  Nova Importação
                </Button>
                <Button type="primary" onClick={resetImportWizard}>
                  Fechar
                </Button>
              </Space>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>Gerenciamento de Rotas</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadRotas}>
            Atualizar
          </Button>
          <Button
            icon={<PrinterOutlined />}
            onClick={() => setPrintModalVisible(true)}
            disabled={!filterCd && !filterRota && !filterUf}
          >
            Imprimir
          </Button>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setImportModalVisible(true)}
          >
            Importar
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            Nova Rota
          </Button>
        </Space>
      </div>

      {/* Campo de Busca por CEP */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Text strong>Buscar Rota por CEP:</Text>
          </Col>
          <Col span={8}>
            <Input
              placeholder="Digite o CEP (8 dígitos)"
              value={searchCep}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 8) {
                  setSearchCep(value);
                }
              }}
              maxLength={8}
              onPressEnter={handleSearchByCep}
            />
          </Col>
          <Col span={10}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearchByCep}
                loading={searchLoading}
                disabled={!searchCep || searchCep.length !== 8}
              >
                Buscar
              </Button>
              <Button onClick={handleClearSearch} disabled={!searchCep}>
                Limpar
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Estatísticas */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total de Rotas (Faixas de Cep)"
              value={statistics.total}
              prefix={<EditOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="CDs Únicos"
              value={statistics.porCd?.length || 0}
              prefix={<EditOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="UFs Únicas"
              value={statistics.porUf?.length || 0}
              prefix={<EditOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabela de Rotas */}
      <Card title="Filtros da Tabela" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={7}>
            <Form.Item label="CD" style={{ marginBottom: 0 }}>
              <Input
                placeholder="Filtrar por CD"
                value={filterCd}
                onChange={(e) => setFilterCd(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={7}>
            <Form.Item label="Rota" style={{ marginBottom: 0 }}>
              <Input
                placeholder="Filtrar por Rota"
                value={filterRota}
                onChange={(e) => setFilterRota(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={7}>
            <Form.Item label="UF" style={{ marginBottom: 0 }}>
              <Input
                placeholder="Filtrar por UF"
                value={filterUf}
                onChange={(e) => setFilterUf(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={3} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              onClick={handleClearFilters}
              disabled={!filterCd && !filterRota && !filterUf}
              style={{ width: '100%' }}
            >
              Limpar
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          dataSource={filteredRotas}
          columns={rotasColumns}
          loading={loading}
          rowKey="id"
          locale={{
            emptyText: !filterCd && !filterRota && !filterUf
              ? 'Use os filtros acima para buscar rotas'
              : 'Nenhuma rota encontrada'
          }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total: ${total} rotas`
          }}
          onChange={(pagination) => {
            setCurrentPage(pagination.current);
            setPageSize(pagination.pageSize);
          }}
        />
      </Card>

      {/* Modal de Criação */}
      <Modal
        title="Nova Rota"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={600}
        maskClosable={false}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateRota}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="CEP Inicial"
                name="cepinicial"
                rules={[
                  { required: true, message: 'CEP inicial é obrigatório' },
                  { len: 8, message: 'CEP deve ter 8 dígitos' }
                ]}
              >
                <Input placeholder="12345678" maxLength={8} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="CEP Final"
                name="cepfinal"
                rules={[
                  { required: true, message: 'CEP final é obrigatório' },
                  { len: 8, message: 'CEP deve ter 8 dígitos' }
                ]}
              >
                <Input placeholder="12345999" maxLength={8} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Praça"
                name="praca"
              >
                <Input placeholder="SP01" maxLength={4} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Rota"
                name="rota"
              >
                <Input placeholder="R001" maxLength={4} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Setor"
                name="setor"
              >
                <InputNumber placeholder="0" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="CD"
                name="cd"
              >
                <Input placeholder="CD001" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="UF"
                name="uf"
              >
                <Input placeholder="SP" maxLength={3} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCreateModalVisible(false);
                createForm.resetFields();
              }}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit">
                Criar
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal de Edição */}
      <Modal
        title="Editar Rota"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setEditingRota(null);
        }}
        footer={null}
        width={600}
        maskClosable={false}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateRota}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="CEP Inicial"
                name="cepinicial"
                rules={[
                  { required: true, message: 'CEP inicial é obrigatório' },
                  { len: 8, message: 'CEP deve ter 8 dígitos' }
                ]}
              >
                <Input placeholder="12345678" maxLength={8} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="CEP Final"
                name="cepfinal"
                rules={[
                  { required: true, message: 'CEP final é obrigatório' },
                  { len: 8, message: 'CEP deve ter 8 dígitos' }
                ]}
              >
                <Input placeholder="12345999" maxLength={8} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Praça"
                name="praca"
              >
                <Input placeholder="SP01" maxLength={4} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Rota"
                name="rota"
              >
                <Input placeholder="R001" maxLength={4} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Setor"
                name="setor"
              >
                <InputNumber placeholder="0" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="CD"
                name="cd"
              >
                <Input placeholder="CD001" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="UF"
                name="uf"
              >
                <Input placeholder="SP" maxLength={3} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setEditModalVisible(false);
                editForm.resetFields();
                setEditingRota(null);
              }}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit">
                Atualizar
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal de Importação - Wizard */}
      <Modal
        title="Importar Rotas"
        open={importModalVisible}
        onCancel={resetImportWizard}
        footer={null}
        width={800}
        style={{
          top: 0,
          maxHeight: 'calc(100vh - 40px)'
        }}
        bodyStyle={{
          maxHeight: 'calc(100vh - 70px)',
          overflowY: 'auto',
          padding: '24px'
        }}
        destroyOnHidden
        maskClosable={false}
      >
        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          <Step title="Upload" description="Carregar arquivo" />
          <Step title="Mapeamento" description="Mapear colunas" />
          <Step title="Importação" description="Processar dados" />
          <Step title="Resultado" description="Verificar resultado" />
        </Steps>

        {renderImportStep()}
      </Modal>

      {/* Modal de Detalhes da Rota - Resultado da Busca por CEP */}
      <Modal
        title="Detalhes da Rota"
        open={searchModalVisible}
        onCancel={() => setSearchModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSearchModalVisible(false)}>
            Fechar
          </Button>
        ]}
        width={600}
        maskClosable={false}
        style={{ top: 0 }}
      >
        {searchResult && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small">
                  <Statistic title="ID" value={searchResult.id} />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic title="Setor" value={searchResult.setor || 0} />
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Card size="small">
                  <div>
                    <Text strong>CEP Inicial:</Text>
                    <br />
                    <Text style={{ fontSize: '16px' }}>{searchResult.cepinicial}</Text>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <div>
                    <Text strong>CEP Final:</Text>
                    <br />
                    <Text style={{ fontSize: '16px' }}>{searchResult.cepfinal}</Text>
                  </div>
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={8}>
                <Card size="small">
                  <div>
                    <Text strong>Rota:</Text>
                    <br />
                    <Text style={{ fontSize: '16px' }}>{searchResult.rota || 'N/A'}</Text>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <div>
                    <Text strong>CD:</Text>
                    <br />
                    <Text style={{ fontSize: '16px' }}>{searchResult.cd || 'N/A'}</Text>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <div>
                    <Text strong>UF:</Text>
                    <br />
                    <Text style={{ fontSize: '16px' }}>{searchResult.uf || 'N/A'}</Text>
                  </div>
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card size="small">
                  <div>
                    <Text strong>Última Atualização:</Text>
                    <br />
                    <Text style={{ fontSize: '16px' }}>
                      {searchResult.dtupdate ? new Date(searchResult.dtupdate).toLocaleString('pt-BR') : 'N/A'}
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Modal de Opções de impressão */}
      <Modal
        title="Opções de impressão"
        open={printModalVisible}
        onCancel={() => setPrintModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setPrintModalVisible(false)}>
            Cancelar
          </Button>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={isPrinting}
          >
            Imprimir
          </Button>
        ]}
        width={400}
      >
        <div style={{ padding: '10px 0' }}>
          <p>Selecione as colunas que deseja incluir no relatório:</p>
          <Space direction="vertical">
            <Checkbox
              checked={printOptions.includeCep}
              onChange={(e) => setPrintOptions(prev => ({ ...prev, includeCep: e.target.checked }))}
            >
              Incluir CEP Inicial e CEP Final
            </Checkbox>
            <Checkbox
              checked={printOptions.includeSetor}
              onChange={(e) => setPrintOptions(prev => ({ ...prev, includeSetor: e.target.checked }))}
            >
              Incluir Setor
            </Checkbox>
          </Space>
          <Alert
            style={{ marginTop: 16 }}
            message="O relatório será gerado com base nos filtros atuais."
            type="info"
            showIcon
          />
        </div>
      </Modal>
    </div>
  );
};

export default RotasManagement;